import { Card } from '@/components/ui/card'
import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { ehFalhaDePedacoVelho, recarregaPorPedacoVelho } from '@/lib/recarga-por-pedaco-velho'
import type { TFunction } from 'i18next'

interface OwnProps {
  children: ReactNode
  fallback?: ReactNode
}

type Props = OwnProps & { t: TFunction }

interface State {
  hasError: boolean
  reloadRequired: boolean
}

/**
 * ErrorBoundary — captures React render errors, reports them to Sentry,
 * and renders an i18n fallback UI instead of crashing the whole panel.
 *
 * A sibling panel that does NOT share the same ErrorBoundary boundary
 * continues to render normally — only the subtree wrapped by this instance
 * is replaced by the fallback.
 *
 * Must remain a class component: React error boundaries require
 * `componentDidCatch` which is only available on class components.
 */
class ErrorBoundaryClass extends Component<Props, State> {
  state: State = { hasError: false, reloadRequired: false }

  static getDerivedStateFromError(error: unknown): State {
    // A tab kept open across deployments can reference a removed lazy chunk.
    // Retrying the chat request cannot repair React.lazy's cached rejection.
    return { hasError: true, reloadRequired: ehFalhaDePedacoVelho(error) }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo)
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack ?? '',
        },
      },
    })
    // CRASH-1458: pedaço velho depois do deploy se conserta recarregando —
    // sozinho, uma vez por janela; dentro dela fica o botão abaixo.
    if (ehFalhaDePedacoVelho(error)) recarregaPorPedacoVelho()
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    const { hasError, reloadRequired } = this.state
    const { children, fallback, t } = this.props

    if (!hasError) {
      return children
    }

    if (fallback !== undefined && !reloadRequired) {
      return fallback
    }

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-red-500/30 bg-axg-bg-tertiary p-8 text-center font-mono"
      >
        <p className="text-base font-semibold text-red-400">{t('errors.boundary.title')}</p>
        <p className="max-w-sm text-sm text-axg-text-muted">{t('errors.boundary.message')}</p>
        <Card.Actions>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-2 rounded border border-axg-accent/40 bg-transparent px-4 py-2 text-sm text-axg-accent transition-colors hover:bg-axg-accent/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-axg-accent"
          >
            {t('errors.boundary.retry')}
          </button>
        </Card.Actions>
      </div>
    )
  }
}

/**
 * ErrorBoundary with i18n wired through `useTranslation` (not the HOC): the
 * boundary now also wraps the side panel (CRASH-1458), and the hook is what
 * every module of the chat already depends on.
 */
export function ErrorBoundary(props: OwnProps): ReactNode {
  const { t } = useTranslation()
  return <ErrorBoundaryClass {...props} t={t} />
}
